import type { DurationUnit } from 'luxon'
import { DateTime } from 'luxon'

import {
  BidType,
  DEFAULT_MARKET_CLOSING_TIME,
  MARKET_CLOSING_WARNING_MINUTES_BEFORE,
  MARKET_TIMEZONE,
  MarketProgram,
  MFRR_MARKET_CLOSING_TIME,
} from '@/features/bidding/constants'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

export enum MarketState {
  OPEN = 'OPEN',
  CLOSING = 'CLOSING',
  CLOSED = 'CLOSED',
}

const isMfrrProgram = (marketProgram?: MarketProgram): boolean =>
  marketProgram === MarketProgram.MFRR_UP || marketProgram === MarketProgram.MFRR_DOWN

/**
 * The capacity market closing time for all market programs is at 17:00, except for mFRR capacity bids which is at 7:30 CE(S)T in the morning. All of them
 * apply to D-1 only, that's the reason why we substract a day from the bid day.
 */
const getMarketClosingTime = (bidDay: MarketDate, marketProgram?: MarketProgram): DateTime => {
  const closingTime = isMfrrProgram(marketProgram) ? MFRR_MARKET_CLOSING_TIME : DEFAULT_MARKET_CLOSING_TIME

  return bidDay.minus({ days: 1 }).getStartOfDay().set(closingTime)
}

const getDurationToMarketClosing = (bidDay: MarketDate, unit: DurationUnit, marketProgram?: MarketProgram): number => {
  const bidClosingTime = getMarketClosingTime(bidDay, marketProgram)
  const nowInMarketTime = DateTime.now().setZone(MARKET_TIMEZONE)

  return bidClosingTime.diff(nowInMarketTime, unit).get(unit)
}

export const hasIntradayBidding = (marketProgram: MarketProgram, bidType: BidType): boolean => {
  return isMfrrProgram(marketProgram) && bidType === BidType.ENERGY
}

/**
 * It compares the bidDay with the current time, and based on that returns a state of the market. This state depends not only on
 * time, but on market programs and bid type.
 *
 * For example, for capacity bids:
 *
 * - All markets expect mFRR -> It closes at 17:00 D-1
 * - mFRR market -> It closes at 7:30 CE(S)T in the morning D-1.
 *
 * And for energy bids:
 *
 * - mFRR market -> It's always open (see the function hasIntradayBidding)
 *
 * An important note!! D-1 means one day before the bidDay parameter.
 */
export const getStateOfMarket = (bidDay: MarketDate, marketProgram: MarketProgram, bidType: BidType): MarketState => {
  if (hasIntradayBidding(marketProgram, bidType)) {
    return MarketState.OPEN
  }

  const minutesUntilClosing = getDurationToMarketClosing(bidDay, 'minutes', marketProgram)

  if (minutesUntilClosing < 0) {
    return MarketState.CLOSED
  } else if (minutesUntilClosing < MARKET_CLOSING_WARNING_MINUTES_BEFORE) {
    return MarketState.CLOSING
  } else {
    return MarketState.OPEN
  }
}

export const getSecondsToMarketClosingWarning = (bidDay: MarketDate, marketProgram?: MarketProgram): number => {
  return getDurationToMarketClosing(bidDay, 'seconds', marketProgram) - MARKET_CLOSING_WARNING_MINUTES_BEFORE * 60
}

const formatTimeWithLocale = (timeObject: Record<string, number>): string => {
  return DateTime.fromObject(timeObject, {
    zone: MARKET_TIMEZONE,
  }).toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

export const formatMarketClosingTimeAsString = (marketProgram?: MarketProgram): string => {
  const closingTime = isMfrrProgram(marketProgram) ? MFRR_MARKET_CLOSING_TIME : DEFAULT_MARKET_CLOSING_TIME
  return formatTimeWithLocale(closingTime)
}

export const formatRemainingTimeAsString = (bidDay: MarketDate, marketProgram?: MarketProgram): string | null => {
  const closing = getMarketClosingTime(bidDay, marketProgram)
  const now = DateTime.now().setZone(MARKET_TIMEZONE)

  return closing.toRelative({
    base: now,
    style: 'short',
    unit: ['hours', 'minutes', 'seconds'],
  })
}
